import * as React from 'react';

import cx from 'classnames';
import { debounce } from 'lodash';
import ReactDOM from 'react-dom';

// @ts-ignore
import Input from 'components/Input';
import LoadingIndicator, { LoadingIndicatorSizeEnum } from 'components/LoadingIndicator';
// @ts-ignore
import ScrollableArea from 'components/ScrollableArea';
import KeyConstants from 'constants/KeyConstants';
import errorLogger from 'utils/errorLogger';
import { findDOMNodeOrThrow } from 'utils/findDOMNodeOrThrow';

import { KeyboardEventHandler } from 'types/typescript/eventHandlers';

import './styles/AsyncSelect.scss';

type Props = {
  // =============
  // = callbacks =
  // =============

  // The function that will be called to load results.
  //   Must take a query string and a callback function.
  //   The search callback must call the callback with a
  //     promise that returns the rendered results to display
  //     in this AsyncSelect
  searchCallback: (
    query: string,
    displayAsyncResults: (promise: Promise<React.ReactElement[]>) => void
  ) => void;

  // Called when the user clicks the x to clear this AsyncSelect
  onClear?: () => void;

  // Called when the user selections an option
  onSelect?: (element: React.ReactElement) => void;

  // Called when user deselects the async select component
  onBlur?: () => void;

  // Called when user clicks on a select option
  onClick?: () => void;

  // ===========
  // = options =
  // ===========

  // The initial item to show in this AsyncSelect
  defaultItem?: React.ReactElement<any>; // eslint-disable-line react/no-unused-prop-types

  // A classname for the select itself
  className?: string;

  // A classname for the results layer
  layerClassName?: string;

  // Whether the items array should be cleared any time the input changes
  clearOnInputChange?: boolean;

  // The number of characters required before searchCallback gets called
  queryMinSize: number;

  // What to show when the query is shorter than queryMinSize
  queryTooShort?: string;

  // Items that will always be included in the results
  permanentItems: React.ReactElement<any>[];

  // Items that will be displayed if there is no query
  initialItems?: React.ReactElement<any>[];

  // A millisecond delay before calling searchCallback. This is used so that
  //   when a user types fast it doesn't make a bunch of useless calls
  inputDelay: number;

  // What to show when there's no results
  noResults: React.ReactElement<any>;

  // What text to show when there's nothing in the input
  placeholder: string;

  // Whether results should continue to load if scrolled to bottom
  infiniteLoad: boolean;

  // What to show when there's no query
  noQuery?: string;
};

export type DefaultProps = {
  queryMinSize: number;
  inputDelay: number;
  permanentItems: React.ReactElement<any>[];
  noResults: React.ReactElement<any>;
  placeholder: string;
  infiniteLoad: boolean;
};

type State = {
  searchString: string;
  items: React.ReactElement[];
  highlight: number;
  pendingQueries: number;
  userTyping: boolean;
  lastSelectedItem?: React.ReactElement<any>;
  showResults: boolean;
};

/*
 * Component to perform async searches.
 * To do so you must register a callback to the component
 * that will be called when a search is need. This registered
 * callback will have two parameters, the search query and a
 * callback. The callback must be called with a promise that
 * returns the search results as AsyncSelectItem.
 *
 * renderSomething(something) {
 *   return (
 *     <AsyncSelectItem id={something.id} title={something.getName()}>
 *       <span>{something.getFirstname()}</span>
 *     </AsyncSelectItem>
 *   );
 * },
 *
 * onSearchCallback(input, callback) {
 *   callback(Something.search(input).then((somethings) => {
 *     return somethings.map(this.renderSomething);
 *   }));
 * },
 *
 * onSelect(id) {
 *   console.log('item selected', id);
 * }
 *
 * render: function() {
 *   return (
 *     <AsyncSelect
 *       searchCallback={this.onSearchCallback}
 *       onSelect={this.onSelect} />
 *   );
 * }
 *
 */

class AsyncSelect extends React.Component<Props, State> {
  static defaultProps: DefaultProps = {
    queryMinSize: 1,
    inputDelay: 0,
    permanentItems: [],
    noResults: <div className="AsyncSelect-searchResults-none">No results</div>,
    placeholder: '',
    infiniteLoad: false,
  };

  constructor(props: Props) {
    super(props);

    this.state = {
      searchString: '',
      items: [],
      highlight: 0,
      pendingQueries: 0,
      userTyping: false,
      lastSelectedItem: undefined,
      showResults: false,
    };
  }

  componentDidMount() {
    const node = findDOMNodeOrThrow(this.refs.select, HTMLElement);
    this._width = node && node.offsetWidth;
    if (this.props.defaultItem) {
      this.setSelectedItem(this.props.defaultItem);
    }
  }

  componentWillReceiveProps(nextProps: Props) {
    if (nextProps.defaultItem && !this.state.lastSelectedItem) {
      this.setSelectedItem(nextProps.defaultItem);
    }
  }

  _width: number | undefined = undefined;
  _isMouseScrolling: boolean = false;

  getInput = (): HTMLInputElement => {
    return findDOMNodeOrThrow((this.refs.input as Input).refs.input, HTMLInputElement);
  };

  handleBlur = () => {
    if (this.props.onBlur) {
      this.props.onBlur();
    }
    let newSearchString = '';
    const { lastSelectedItem } = this.state;
    if (lastSelectedItem) {
      newSearchString = lastSelectedItem.props.title;
    }
    this.clearResults();
    this.setState({
      searchString: newSearchString,
      showResults: false,
    });
  };

  handleClick = () => {
    if (this.props.onClick) {
      this.props.onClick();
    }
  };

  onFocus = () => {
    if (!this.state.lastSelectedItem) {
      this.setState({ showResults: true });
    }
  };

  onInputChange = () => {
    const node = this.getInput();
    const { value } = node;
    const { queryMinSize } = this.props;

    this.setState({ searchString: value });
    if (queryMinSize && value.length >= queryMinSize) {
      this.setState({
        showResults: true,
        userTyping: true,
      });
      this.callSearchCallback(value);
    } else if (value.length === 0) {
      this.clearResults();
    }

    if (this.props.clearOnInputChange) {
      if (this.props.onClear) {
        this.props.onClear();
      }

      this.clearResults();
    }
  };

  onInputFocus = () => {
    const { lastSelectedItem } = this.state;
    if (lastSelectedItem && this.state.searchString === lastSelectedItem.props.title) {
      this.clearResults();
    }
  };

  onResultClick = (item: React.ReactElement<any>) => {
    this.onResultSelected(item);
  };

  onResultSelected = (item: React.ReactElement<any>) => {
    if (!this.isItemPermanent(item)) {
      this.setState({
        searchString: item.props.title,
        lastSelectedItem: item,
      });
    }

    if (this.props.onSelect) {
      this.props.onSelect(item);
    }

    this.clearResults();
    this.setState({
      showResults: false,
    });
  };

  getSearchString = () => {
    return this.state.searchString;
  };

  setSelectedItem = (item: React.ReactElement<any>) => {
    const input = this.getInput();
    input.value = item.props.title;
    this.setState({
      searchString: item.props.title,
      lastSelectedItem: item,
    });
  };

  clearInput = () => {
    this.setState({
      searchString: '',
    });
  };

  clearResults = () => {
    this.setState({
      highlight: 0,
      items: [],
    });
  };

  displayAsyncResults = (promise: Promise<React.ReactElement[]>) => {
    this.setState({
      userTyping: false,
      pendingQueries: this.state.pendingQueries + 1,
    });
    promise
      .then(items => {
        this.setState({
          pendingQueries: this.state.pendingQueries - 1,
          items,
        });
      })
      .catch(errorLogger('Display async results failure'));
  };

  searchAndDisplay = (query: string) => this.props.searchCallback(query, this.displayAsyncResults);

  callSearchCallback = debounce(this.searchAndDisplay, this.props.inputDelay);

  handleInputKeyDown: KeyboardEventHandler = event => {
    const code = event.keyCode;
    switch (code) {
      case KeyConstants.ENTER: {
        const item = this.state.items[this.state.highlight];
        if (item) {
          this.onResultSelected(item);
        }
        break;
      }
      case KeyConstants.UP:
      case KeyConstants.DOWN: {
        let { highlight } = this.state;
        if (code === KeyConstants.UP) {
          highlight = highlight === 0 ? this.state.items.length - 1 : highlight - 1;
        } else {
          highlight = highlight === this.state.items.length - 1 ? 0 : highlight + 1;
        }
        this.setState({ highlight }, () => {
          const searchItemNode = ReactDOM.findDOMNode(
            this.refs[`item-${highlight}`]
          ) as HTMLElement;
          (this.refs.scrollArea as ScrollableArea).scrollIntoView(searchItemNode);
        });

        break;
      }
      default:
        return;
    }
    event.preventDefault();
  };

  handleResultMouseOver = (index: number) => {
    if (this._isMouseScrolling) {
      this._isMouseScrolling = false;
      return;
    }
    this.setState({ highlight: index });

    const { queryMinSize } = this.props;
    if (
      this.props.infiniteLoad &&
      this.isScrolledToBottom() &&
      queryMinSize &&
      this.state.searchString.length >= queryMinSize
    ) {
      this.callSearchCallback(this.state.searchString);
    }
  };

  hasItems = () => {
    return this.state.items && this.state.items.length > 0;
  };

  isItemPermanent = (item: React.ReactElement<any>) => {
    const { permanentItems } = this.props;
    return (
      permanentItems &&
      permanentItems.find(result => {
        return result.props.id === item.props.id;
      })
    );
  };

  isScrolledToBottom = () => {
    return (this.refs.scrollArea as ScrollableArea).isScrolledToBottom(150);
  };

  focus = () => {
    const input = this.getInput();
    input.focus();
  };

  waitingOnQueries = () => {
    return this.state.userTyping === true || this.state.pendingQueries > 0;
  };

  renderLoadingIndicator() {
    if (this.waitingOnQueries()) {
      return (
        <div className="AsyncSelect-searchLoading">
          <LoadingIndicator size={LoadingIndicatorSizeEnum.small} />
        </div>
      );
    } else {
      return null;
    }
  }

  renderNoResultsOrQuery() {
    const query = this.state.searchString;
    const { queryMinSize } = this.props;

    if (this.state.items.length === 0 && !this.waitingOnQueries()) {
      if (query === '') {
        return (
          this.props.noQuery && <div className="AsyncSelect-noQuery">{this.props.noQuery}</div>
        );
      } else if (queryMinSize && query.length < queryMinSize && this.props.queryTooShort) {
        return <div className="AsyncSelect-noQuery">{this.props.queryTooShort}</div>;
      } else {
        return this.props.noResults;
      }
    }

    return null;
  }

  renderPermanent = (item: React.ReactElement<any>, index: number) => {
    return (
      <div
        className={cx('AsyncSelect-searchResult-permanent')}
        key={item.props.id || `permanent-${index}`}
        onMouseDown={() => this.onResultClick(item)}>
        {item}
      </div>
    );
  };

  renderResult = (item: React.ReactElement<any>, index: number) => {
    let moved = false;
    return (
      <div
        data-test-selector={`search-result-${index}`}
        className={cx('AsyncSelect-searchResult', {
          highlight: index === this.state.highlight,
        })}
        ref={`item-${index}`}
        key={item.props.id || `item-${index}`}
        onMouseOver={() => this.handleResultMouseOver(index)}
        onTouchStart={() => this.handleResultMouseOver(index)}
        onTouchMove={() => {
          moved = true;
        }}
        onTouchEnd={() => {
          if (!moved) {
            this.onResultClick(item);
          }
          moved = false;
        }}
        onMouseDown={() => this.onResultClick(item)}>
        {item}
      </div>
    );
  };

  renderResults(layerClassName?: string) {
    if (!this.state.showResults) {
      return null;
    }

    if (!this.props.noQuery && !this.props.initialItems && this.state.searchString === '') {
      return null;
    }

    let permanentItems;
    if (this.props.permanentItems) {
      permanentItems = <div>{this.props.permanentItems.map(this.renderPermanent)}</div>;
    }

    let resultingItems;
    if (this.hasItems() || this.waitingOnQueries()) {
      resultingItems = (
        <ScrollableArea className="AsyncSelect-list" ref="scrollArea" useBodyChildrenHeight>
          {this.state.items.map(this.renderResult)}
          {this.renderLoadingIndicator()}
        </ScrollableArea>
      );
    } else if (this.props.initialItems && !this.state.searchString) {
      resultingItems = (
        <ScrollableArea className="AsyncSelect-list" ref="scrollArea" useBodyChildrenHeight>
          {this.props.initialItems.map(this.renderResult)}
        </ScrollableArea>
      );
    }

    return (
      <div className="AsyncSelect-resultsWrapper">
        <div
          className={cx('AsyncSelect-searchResults', layerClassName)}
          style={{
            width: this._width || undefined,
          }}>
          {this.renderNoResultsOrQuery()}
          {resultingItems}
          <div className="AsyncSelect-searchResults-permanent">{permanentItems}</div>
        </div>
      </div>
    );
  }

  render() {
    const { className, layerClassName, onSelect, ...others } = this.props; // eslint-disable-line

    return (
      <div
        ref="select"
        className={cx('AsyncSelect', className)}
        onBlur={this.handleBlur}
        onClick={this.handleClick}
        onFocus={this.onFocus}>
        <Input
          {...others}
          glyph="search"
          ref="input"
          className="AsyncSelect-input"
          placeholder={this.props.placeholder}
          onKeyDown={this.handleInputKeyDown}
          onChange={this.onInputChange}
          onFocus={this.onInputFocus}
          value={this.state.searchString || ''}
        />
        {this.renderResults(layerClassName)}
      </div>
    );
  }
}

export default AsyncSelect;
